// Wrong-choice reactions in one bit (studio/ART-BIBLE.md): they play behind the reeded glass.
//   alien    let in   : it stretches in jumps, its eyes go red and multiply, long hands press on the glass
//   infected let in   : it glows plague green, sores show, and it doubles over coughing green clouds
//   human    kept out : they turn their back and walk off into the snow, leaving prints
// Motion is stepped at 15 fps: every pose is a pure function of the frame number, never a smooth tween.
// The colour reactions (the old look) live in reactions-colour.js.
import { GLASS, silhouetteMask, drawGlass, makeSilhouette, seedOf } from './visitors.js';
import { pickEye, eyeLook } from './eyes.js';
import { knockerFrame, screamFrame, skinSheet, SHEET_BASE } from './knocker.js';
import { createBits, canvasGlyphs, dz, T, W, H } from './bits.js';

export const FPS = 15;
export const REACTION_SECONDS = 5;

// The reaction a reveal plays: only a wrong call gets one.
export function reactionFor(kind, verdict) {
  if (kind === 'human') return verdict === 'shut' ? 'human' : null;
  if (kind === 'alien' || kind === 'infected') return verdict === 'open' ? kind : null;
  return null;
}

const hash = (a, b) => { let h = (a * 374761393 + b * 668265263) | 0; h = Math.imul(h ^ (h >>> 13), 1274126177); return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };
const calm = () => ({
  stretch: 0, eyes: 'plain', extra: 0, hands: false, glitch: false,
  glow: 0, sores: false, hunch: 0, cough: false, puffs: [],
  back: false, k: 1, stride: 0, gone: false, trail: [], snow: 1, ground: false,
});
// frame boundaries in frames (15 per second)
// the refused human: the head drops (frames 2-5), they turn their back (6) and set off (8)
const TURN_AT = 6, WALK_FROM = 8, WALK_FRAMES = 56, GONE_AT = 69, STEP = 3, COUGHS = [15, 33];

// The pose t seconds after the reveal (t < 0: before it, and every kind looks the same).
export function revealPose(kind, verdict, t) {
  const p = calm();
  if (!kind || t == null || t < 0) return p;
  const f = Math.floor(t * FPS + 1e-6); // the frame; everything below is whole frames
  const wrong = reactionFor(kind, verdict) === kind;
  if (kind === 'alien') {
    if (f >= 8) p.eyes = 'red';
    if (!wrong) return p; // a right call only shows what it was
    p.glitch = f >= 3 && f < 18 && hash(f, 41) < 0.6;
    p.stretch = f < 6 ? 0 : Math.min(4, Math.floor((f - 6) / 6) + 1) / 4; // four jumps, 0.4 s apart
    p.extra = f < 11 ? 0 : Math.min(EXTRA.length, (Math.floor((f - 11) / 4) + 1) * 2); // they open in pairs
    p.hands = f >= 15;
    return p;
  }
  if (kind === 'infected') {
    p.eyes = 'sick';
    if (!wrong) { if (f >= 8) p.glow = 0.25; return p; }
    p.glow = f < 3 ? 0 : Math.min(4, Math.floor((f - 3) / 3) + 1) / 4;
    p.sores = f >= 8;
    for (const c of COUGHS) {
      const a = f - c;
      if (a >= 0 && a < 8) { p.cough = true; p.hunch = a < 3 ? 2 : 1; }
      if (a >= 0 && a < 14) p.puffs.push(
        { dx: 14 + a * 5, dy: -4 - a * 2, r: 6 + a * 1.6, dens: 1 - a / 14 },
        { dx: -12 - a * 4, dy: 2 - a, r: 4 + a * 1.2, dens: 0.85 - a / 18 },
      );
    }
    return p;
  }
  if (kind === 'human') {
    if (!wrong) return p;
    if (f >= 2 && f < TURN_AT) p.hunch = 1; // the head drops
    p.back = f >= TURN_AT;
    p.ground = true;
    p.snow = 1 + Math.min(1, f / 45);
    if (f >= GONE_AT) p.gone = true;
    if (f >= WALK_FROM) {
      // perspective at a steady walk (size = 1 / distance), so the figure is still 40+ px tall at 2.4 s;
      // in the last second the snow swallows them and they dwindle fast
      const kAt = g => { const n = Math.min(WALK_FRAMES, g - WALK_FROM), late = Math.max(0, n - 37); return 1 / (1 + 0.16 * n + 0.05 * late * late); };
      p.k = kAt(f);
      const steps = Math.floor((f - WALK_FROM) / STEP); // a brisk walk: a step every STEP frames, a print per step
      p.stride = f >= WALK_FROM + WALK_FRAMES ? 0 : (steps % 2) + 1;
      // a print where each foot was planted, the newest one just behind the heel that is lifting now
      for (let i = 0; i <= steps; i++) { const g = WALK_FROM + i * STEP; if (g < f) p.trail.push({ k: kAt(g), side: i % 2 ? 1 : -1 }); }
    }
    return p;
  }
  return p;
}

// more eyes (head units: the head is 26 across, after 02-one-bit-events.html): a wider pair set higher and out
// towards the temples, a small pair near the crown, then one in the middle of the brow. They fan out from the
// first pair like a spider's, never stacking in a column under it.
const EXTRA = [[-17, -11], [17, -11], [-7, -31], [7, -31], [0, -20]];

// Everything drawGlass (visitors.js) needs for one frame: where the figure stands, its eyes, clouds and prints.
// approach 0..1: far and faint (just knocked) to up against the glass. jitter: extra sideways sway (nerves).
// knocker: { base, reduced } on the door (plain, gold, red and costumed knocks): an alien's head becomes the Knocker
// (knocker.js) KNOCKER_AT frames into its reveal, its dome sitting on glass row `base`. Only after the verdict (kind is
// only known then); bull, bear and lit knocks have their own states and never get it.
// wrong (sprint 33 item 2, the plan's flood and scream flags in call-lands.js): a wrong Open's red moment on plain, gold and red knocks. The red Bayer
// flood, the cracks and the Knocker's scream (the lunge sheet, holding A3's freeze frame) run from the land until the card. creep: the sheet frame of the last 10 s creep.
export function glassState({ sil = null, kind = null, verdict = null, revealT = -1, fr = 0, t = 0, outbreak = false,
  lamp = 1, approach = 1, jitter = 0, blink = false, redEyes = false, eye = null, slip = false, knocker = null, wrong = null, creep = null } = {}) {
  const pose = revealPose(kind, verdict, revealT);
  const gs = { lamp, fr, t, outbreak: !!outbreak, glitch: pose.glitch && !knocker?.reduced, snow: pose.snow, ground: pose.ground,
    glow: pose.glow, sores: pose.sores, hidden: pose.gone, M: null, eyes: [], eyesOn: false, eyeTint: T.none, puffs: [], prints: [] };
  if (!sil) return gs;
  // Halloween: the costume comes off at the reveal, so what shows is the person (or the thing) underneath
  if (sil.costume && kind && revealT >= 0) sil = { ...sil, costume: null };
  const M = silhouetteMask(sil, { stretch: pose.stretch, hands: pose.hands, back: pose.back, stride: pose.stride, hunch: pose.hunch, t });
  const base = 0.62 + 0.38 * approach;
  const hv0 = GLASS.foot - (GLASS.foot - GLASS.hy + sil.rise) * base;
  // perspective about a vanishing point just above the snow horizon: walking off, the head stays near eye level
  // while the feet climb towards the horizon
  const VP = GLASS.horizon - 6;
  const place = k => ({ s: base * k, ax: GLASS.hx + (1 - k) * 28, hv: VP + (hv0 - VP) * k });
  const p = place(pose.k);
  let sway = Math.sin(t * 1.3) * 0.8 + jitter;
  if (pose.cough) sway += fr & 1 ? 2 : -2;
  // approach over 1 is the lunge (call-lands.js): bigger, pressed to the glass; presence stops at fully there
  Object.assign(gs, { M, sx: p.s * (1 - 0.08 * pose.stretch), sy: p.s, ax: p.ax + sway * pose.k, hv: p.hv, pres: Math.min(1, 0.32 + 0.68 * approach),
    crisp: pose.back && pose.k < 0.75 });
  const [hx, hy, rx] = M.head;
  // the head's centre lands on a whole glass pixel, so every pair of eyes is drawn level and mirror-symmetric
  const c0 = gs.ax + (hx - M.cx) * gs.sx, c = Math.round(c0);
  gs.ax += c - c0;
  const toGlass = (mx, my) => [gs.ax + (mx - M.cx) * gs.sx, gs.hv + (my - M.oy) * gs.sy];
  const extra = EXTRA.slice(0, pose.extra).map(([a, b]) => [hx + (a * rx) / 26, hy + (b * rx) / 26]);
  const toEye = ([x, y]) => { const d = (x - hx) * gs.sx; return [c + Math.sign(d) * Math.round(Math.abs(d)), Math.round(gs.hv + (y - M.oy) * gs.sy)]; };
  // an extra eye whose whole box would not fit inside the arch is left out (sprint 32 note: the fifth showed as a lone red dash above the head)
  gs.eyes = [...M.eyes.map(toEye), ...extra.map(toEye).filter(eyeFits)];
  gs.eyesOn = gs.pres > 0.85 && !blink && !pose.gone;
  gs.eyeTint = pose.eyes === 'red' || redEyes ? T.red : outbreak || pose.eyes === 'sick' ? T.green : T.none;
  // the costume's eye holes take the small eyes; a costume is gone at any reveal
  gs.eye = pickEye(sil.costume ? 'S' : 'L', eye, eyeLook(gs.eyeTint === T.red, pose.eyes === 'sick', kind, revealT), slip);
  gs.puffs = pose.puffs.map(q => { const [u, v] = toGlass(M.mouth[0] + q.dx, M.mouth[1] + q.dy); return { u, v, r: Math.max(2, Math.round(q.r * gs.sx)), dens: q.dens }; });
  gs.prints = pose.trail.map(({ k, side }) => { const q = place(k); return [Math.round(q.ax + side * 16 * q.s), Math.round(q.hv + (M.feet - M.oy) * q.s), Math.max(2, Math.round(24 * q.s)), Math.max(1, Math.round(10 * q.s))]; });
  // the Knocker: the person (and its eyes) gives way to the Aseprite Knocker, which brings its own eyes, teeth, fingers and bloom
  // the wrong Open: from the first frame after the land the red flood, the cracks and, for an alien, the Knocker's scream, which holds its
  // last frame (A3's freeze) until the card; the grin below is a right call's. An infected person is flooded where it stands.
  const sf = wrong && (kind === 'alien' || kind === 'infected') && revealT >= 0 ? screamFrame(Math.floor(revealT * FPS + 1e-6), sil.fail) : null;
  if (sf != null) {
    gs.cracks = { f: sf };
    if (wrong.scream && kind === 'alien') { gs.scream = { f: sf, flood: !!wrong.flood, skin: skinSheet(sil.fail) ? sil.fail : null }; gs.hidden = true; gs.eyesOn = false; gs.puffs = []; }
    else if (wrong.flood) gs.flood = { f: sf };
  }
  const kf = knocker && kind === 'alien' && revealT >= 0 && !gs.scream ? knockerFrame(Math.floor(revealT * FPS + 1e-6), !!knocker.reduced) : null;
  if (kf != null) {
    gs.knocker = { f: kf, base: knocker.base ?? SHEET_BASE, handsBase: knocker.handsBase ?? knocker.base ?? SHEET_BASE };
    gs.hidden = true; gs.eyesOn = false; gs.puffs = [];
  }
  if (creep != null) gs.creep = { f: creep };
  return gs;
}

// the box of an L eye (eyes-L sheet: 15 x 13, its point at 7, 7) lies wholly inside the glass's arch
const inArch = (u, v) => { const r = GLASS.w / 2; if (u < 0 || u >= GLASS.w || v < 0 || v >= GLASS.h) return false; return v >= r || Math.hypot(u - r + 0.5, v - r) < r; };
export const eyeFits = ([x, y]) => inArch(x - 7, y - 7) && inArch(x + 7, y - 7) && inArch(x - 7, y + 5) && inArch(x + 7, y + 5);

// The porch lamp during a reveal: it stutters, then burns harsh for an alien or the infected.
export function revealLamp(kind, revealT, fr, t) {
  if (!kind || revealT < 0) return 1;
  if (revealT < 0.4) return hash(fr, 3) < 0.5 ? 0.15 : 1.15;
  return kind === 'alien' || kind === 'infected' ? 1.12 + Math.sin(t * 9) * 0.04 : 1;
}

// ======================= the sheet (client/reactions.html) =======================
// Three windows, one per wrong call, each looping: the visitor at the glass, the call, the reaction.
// ?t=<seconds after the call> freezes every window on that frame (for stills and screenshots).
const PANELS = [
  { kind: 'alien', verdict: 'open', title: 'Opened to an alien', head: 'beanie', outbreak: false,
    lines: ['It stretches, too tall.', 'Red eyes open, then more.'], score: '-100  A house goes dark.' },
  { kind: 'infected', verdict: 'open', title: 'Opened to the infected', head: 'hood', outbreak: true,
    lines: ['It glows green. Sores.', 'It doubles over, coughing.'], score: '-160 (x2)  A flat is sick.' },
  { kind: 'human', verdict: 'shut', title: 'Kept a human out', head: 'hat', outbreak: false,
    lines: ['They turn their back and', 'walk off into the snow.'], score: '-100  Left in the cold.' },
];
const LOOP = 1.5 + REACTION_SECONDS + 1; // seconds: at the glass, the reaction, a held last frame

export function findSilhouette(head, pred = () => true) {
  for (let i = 0; i < 400; i++) { const s = makeSilhouette(seedOf(`sheet-${head}-${i}`)); if (s.head === head && pred(s)) return s; }
  return makeSilhouette(seedOf(head));
}

export function mountReactionSheet(canvas, { frozen = null, doc = document } = {}) {
  canvas.width = W; canvas.height = H;
  const g = canvas.getContext('2d');
  const img = g.createImageData(W, H), d32 = new Uint32Array(img.data.buffer);
  const B = createBits(W, H, canvasGlyphs(doc));
  const { fill, hline, vline, box, px, text, tw, S } = B;
  const sils = PANELS.map(p => findSilhouette(p.head, s => s.height === 'average' && (p.kind !== 'human' || !s.scarf)));
  function windowFrame(x, y, w, h, title) {
    fill(x + 2, y + 2, w, h, 0); fill(x, y, w, h, 1); box(x, y, w, h, 0);
    for (let j = y + 3; j < y + 14; j += 2) hline(x + 3, j, w - 6, 0);
    hline(x, y + 16, w, 0);
    const tW = tw(title, 'mid'); fill(Math.round(x + w / 2 - tW / 2 - 6), y + 1, tW + 12, 15, 1);
    text(title, x + w / 2, y + 2, 'mid', 0, 'center');
  }
  function draw(now) {
    const fr = Math.floor((now / 1000) * FPS), t = fr / FPS;
    const loopT = frozen != null ? null : (fr % Math.round(LOOP * FPS)) / FPS;
    const revealT = frozen != null ? frozen : loopT - 1.5;
    S.tint = 0; S.ox = 0; S.clip = null;
    fill(0, 0, W, H, (x, y) => dz(x, y, 0.06));
    fill(0, 0, W, 17, 1); hline(0, 17, W, 0);
    fill(10, 3, 9, 12, 0); fill(12, 5, 5, 4, 1); px(17, 10, 1);
    text('Wrong choices', 28, 2, 'mid', 0);
    const clock = revealT < 0 ? 'at the glass' : `${revealT.toFixed(2)} s after the call, frame ${Math.round(revealT * FPS)}`;
    text(`${clock}  ·  15 fps`, W - 8, 2, 'mid', 0, 'right');
    PANELS.forEach((p, i) => {
      const x = 8 + i * 210, y = 26, w = 204, h = 300;
      windowFrame(x, y, w, h, p.title);
      fill(x + 1, y + 17, w - 2, 180, (a, b) => dz(a, b, 0.05));
      const fq = Math.max(0, Math.floor(revealT * FPS));
      const lamp = revealLamp(p.kind, revealT, fr, t);
      drawGlass(B, x + 12, y + 24, glassState({ sil: sils[i], kind: p.kind, verdict: p.verdict, revealT, fr: revealT >= 0 ? fq : fr, t, outbreak: p.outbreak, lamp }));
      hline(x + 1, y + 197, w - 2, 0);
      p.lines.forEach((l, j) => text(l, x + 10, y + 201 + j * 13, 'mid', 0));
      text(p.score, x + 10, y + 230, 'mid', 0);
      // the timeline: one tick per frame, the current frame filled in
      const tx = x + 10, ty = y + 256, tl = w - 20, frames = REACTION_SECONDS * FPS;
      text('0 s', tx, ty + 12, 'small', 0); text(`${REACTION_SECONDS} s`, tx + tl, ty + 12, 'small', 0, 'right');
      hline(tx, ty + 6, tl, 0);
      for (let k = 0; k <= frames; k += 1) { const xx = tx + Math.round((k * (tl - 1)) / frames); vline(xx, ty + (k % FPS === 0 ? 2 : 4), k % FPS === 0 ? 8 : 4, 0); }
      if (revealT >= 0) { const xx = tx + Math.round((Math.min(frames, revealT * FPS) * (tl - 1)) / frames); fill(xx - 2, ty - 2, 5, 4, 0); vline(xx, ty, 12, 0); }
      const tag = p.outbreak ? 'x2' : '';
      if (tag) { const tgw = tw(tag, 'small') + 10; S.tint = T.green; fill(x + w - 14 - tgw, y + 230, tgw, 13, 1); S.tint = 0; box(x + w - 14 - tgw, y + 230, tgw, 13, 0); text(tag, x + w - 14 - tgw / 2, y + 233, 'small', 0, 'center'); }
    });
    fill(0, H - 14, W, 14, 1); hline(0, H - 14, W, 0);
    text('Not an investment. The coin can go to zero. Prizes go to the best players of each night shift.', W / 2, H - 10, 'small', 0, 'center');
    B.blit(d32);
    g.putImageData(img, 0, 0);
  }
  let last = -1, lastNow = 0;
  function tick(now) {
    const f = Math.floor((now / 1000) * FPS);
    if (f !== last) { last = f; lastNow = now; draw(now); } // stepped: redraw only on a new 15 fps frame
    requestAnimationFrame(tick); // frozen too: the pose holds, the snow keeps falling
  }
  tick(performance.now());
  return { draw, redraw: () => draw(lastNow) };
}
